import loadable from '@loadable/component';
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import React from 'react';

import NamedRedirect from './components/NamedRedirect/NamedRedirect';
// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import getPageDataLoadingAPI from './containers/pageDataLoadingAPI';
import redirects from './redirects';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const LoyaltyProgrammePage = loadable(
  () =>
    import(
      /* webpackChunkName: "LoyaltyProgrammePage" */ './containers/LoyaltyProgrammePage/LoyaltyProgrammePage'
    )
);
const BookmarkPage = loadable(
  () => import(/* webpackChunkName: "BookmarkPage" */ './containers/BookmarkPage/BookmarkPage')
);
const CalendarPage = loadable(
  () => import(/* webpackChunkName: "CalendarPage" */ './containers/CalendarPage/CalendarPage')
);
const CheckoutPage = loadable(
  () => import(/* webpackChunkName: "CheckoutPage" */ './containers/CheckoutPage/CheckoutPage')
);
const ContactSettingsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "ContactSettingsPage" */ './containers/ContactSettingsPage/ContactSettingsPage'
    )
);
const DeleteUserPage = loadable(
  () =>
    import(/* webpackChunkName: "DeleteUserPage" */ './containers/DeleteUserPage/DeleteUserPage')
);
const EditListingPage = loadable(
  () =>
    import(/* webpackChunkName: "EditListingPage" */ './containers/EditListingPage/EditListingPage')
);
const EmailVerificationPage = loadable(
  () =>
    import(
      /* webpackChunkName: "EmailVerificationPage" */ './containers/EmailVerificationPage/EmailVerificationPage'
    )
);
const InboxPage = loadable(
  () => import(/* webpackChunkName: "InboxPage" */ './containers/InboxPage/InboxPage')
);
const LandingPage = loadable(
  () => import(/* webpackChunkName: "LandingPage" */ './containers/LandingPage/LandingPage')
);
const ListingPage = loadable(
  () => import(/* webpackChunkName: "ListingPage" */ './containers/ListingPage/ListingPage')
);
const ManageListingsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "ManageListingsPage" */ './containers/ManageListingsPage/ManageListingsPage'
    )
);
const PasswordChangePage = loadable(
  () =>
    import(
      /* webpackChunkName: "PasswordChangePage" */ './containers/PasswordChangePage/PasswordChangePage'
    )
);
const PasswordRecoveryPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PasswordRecoveryPage" */ './containers/PasswordRecoveryPage/PasswordRecoveryPage'
    )
);
const PasswordResetPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PasswordResetPage" */ './containers/PasswordResetPage/PasswordResetPage'
    )
);
const PaymentMethodsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PaymentMethodsPage" */ './containers/PaymentMethodsPage/PaymentMethodsPage'
    )
);
const ProfilePage = loadable(
  () => import(/* webpackChunkName: "ProfilePage" */ './containers/ProfilePage/ProfilePage')
);
const ProfileSettingsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "ProfileSettingsPage" */ './containers/ProfileSettingsPage/ProfileSettingsPage'
    )
);
const RoutePlannerPage = loadable(
  () =>
    import(
      /* webpackChunkName: "RoutePlannerPage" */ './containers/RoutePlannerPage/RoutePlannerPage'
    )
);
const SearchPage = loadable(
  () => import(/* webpackChunkName: "SearchPage" */ './containers/SearchPage/SearchPage')
);
const SigninPage = loadable(
  () => import(/* webpackChunkName: "SigninPage" */ './containers/SigninPage/SigninPage')
);
const SignupPage = loadable(
  () => import(/* webpackChunkName: "SignupPage" */ './containers/SignupPage/SignupPage')
);
const PassSettingsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "PassSettingsPage" */ './containers/PassSettingsPage/PassSettingsPage'
    )
);
const StripePayoutPage = loadable(
  () =>
    import(
      /* webpackChunkName: "StripePayoutPage" */ './containers/StripePayoutPage/StripePayoutPage'
    )
);
const TransactionPage = loadable(
  () =>
    import(/* webpackChunkName: "TransactionPage" */ './containers/TransactionPage/TransactionPage')
);

const DumpStationSearchPage = loadable(
  () =>
    import(
      /* webpackChunkName: "DumpStationSearchPage" */ './containers/DumpStationSearchPage/DumpStationSearchPage'
    )
);
const DumpStationPage = loadable(
  () =>
    import(/* webpackChunkName: "DumpStationPage" */ './containers/DumpStationPage/DumpStationPage')
);

const StoryblokPage = loadable(
  () => import(/* webpackChunkName: "StoryblokPage" */ './containers/StoryblokPage/StoryblokPage')
);

const NotFoundPage = loadable(
  () => import(/* webpackChunkName: "NotFoundPage" */ './containers/NotFoundPage/NotFoundPage')
);

const NotificationSettingsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "NotificationSettingsPage" */ './containers/NotificationSettingsPage/NotificationSettingsPage'
    )
);

const NewListingTypePage = loadable(
  () =>
    import(
      /* webpackChunkName: "NewListingTypePage" */ './containers/NewListingTypePage/NewListingTypePage'
    )
);

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactSettingsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'NotificationSettingsPage',
];

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

const { LatLng, LatLngBounds } = sdkTypes;

const location = {
  address: 'Deutschland',
  bounds: new LatLngBounds(
    new LatLng(55.1145032951348, 15.0418329997963),
    new LatLng(47.2702370080945, 5.86631401478655)
  ),
};
const searchParams = {
  address: location.address,
  bounds: location.bounds,
};

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.

/**
 * Route configuration for VanSite
 * @returns {Array<import('../types/vansite').VanSite.Route>} Route configuration
 */
const routeConfiguration = () => {
  return [
    ...redirects,
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadDuck: pageDataLoadingAPI.LandingPage.loadDuck,
    },
    {
      path: '/bookmarks',
      name: 'BookmarkPage',
      auth: true,
      authPage: 'SignupPage',
      component: BookmarkPage,
      loadDuck: pageDataLoadingAPI.BookmarkPage.loadDuck,
    },
    {
      path: '/s',
      name: 'SearchPage',
      component: SearchPage,
      loadDuck: pageDataLoadingAPI.SearchPage.loadDuck,
    },
    {
      path: '/route-planner',
      name: 'RoutePlannerPage',
      component: RoutePlannerPage,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadDuck: pageDataLoadingAPI.ListingPage.loadDuck,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      loadDuck: pageDataLoadingAPI.CheckoutPage.loadDuck,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.loadDuck,
    },
    {
      path: '/l/:category/:slug/:id',
      name: 'ListingPageUrlCategory',
      component: ListingPage,
      loadDuck: pageDataLoadingAPI.ListingPage.loadDuck,
    },
    {
      path: '/l/:category/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'SigninPage',
      component: ListingPage,
      loadDuck: pageDataLoadingAPI.ListingPage.loadDuck,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      authPage: 'SigninPage',
      component: NewListingTypePage,
    },
    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadDuck: pageDataLoadingAPI.ListingPage.loadDuck,
    },
    {
      path: '/e/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadDuck: pageDataLoadingAPI.EditListingPage.loadDuck,
    },
    {
      path: '/e/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadDuck: pageDataLoadingAPI.EditListingPage.loadDuck,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadDuck: pageDataLoadingAPI.ProfilePage.loadDuck,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'SigninPage',
      component: ProfileSettingsPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the SigninPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'SigninPage',
      component: SigninPage,
      loadDuck: pageDataLoadingAPI.SigninPage.loadDuck,
    },
    {
      path: '/points',
      name: 'LoyaltyProgrammePage',
      auth: true,
      authPage: 'SigninPage',
      component: LoyaltyProgrammePage,
      loadDuck: pageDataLoadingAPI.LoyaltyProgrammePage.loadDuck,
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: SignupPage,
      loadDuck: pageDataLoadingAPI.SignupPage.loadDuck,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'SignupPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'SignupPage',
      component: InboxPage,
      loadDuck: pageDataLoadingAPI.InboxPage.loadDuck,
    },
    {
      path: '/order/:id',
      name: 'OrderPage',
      auth: true,
      authPage: 'SigninPage',
      component: (props) => <NamedRedirect name="OrderDetailsPage" params={{ ...props.params }} />,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'SigninPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      paramsTransform: (params) => ({ ...params, transactionRole: 'customer' }),
      loadDuck: pageDataLoadingAPI.TransactionPage.loadDuck,
      setInitialValues: pageDataLoadingAPI.TransactionPage.loadDuck,
    },
    {
      path: '/sale/:id',
      name: 'SalePage',
      auth: true,
      authPage: 'SigninPage',
      component: (props) => <NamedRedirect name="SaleDetailsPage" params={{ ...props.params }} />,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'SigninPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      paramsTransform: (params) => ({ ...params, transactionRole: 'provider' }),
      loadDuck: pageDataLoadingAPI.TransactionPage.loadDuck,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'SignupPage',
      component: ManageListingsPage,
      loadDuck: pageDataLoadingAPI.ManageListingsPage.loadDuck,
    },
    {
      path: '/calendar',
      name: 'CalendarPage',
      auth: true,
      authPage: 'SignupPage',
      component: CalendarPage,
      loadDuck: pageDataLoadingAPI.CalendarPage.loadDuck,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'SignupPage',
      component: () => <NamedRedirect name="ContactSettingsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactSettingsPage',
      auth: true,
      authPage: 'SigninPage',
      component: ContactSettingsPage,
      loadDuck: pageDataLoadingAPI.ContactSettingsPage.loadDuck,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'SigninPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'SigninPage',
      component: StripePayoutPage,
      loadDuck: pageDataLoadingAPI.StripePayoutPage.loadDuck,
    },
    {
      path: '/account/notification',
      name: 'NotificationSettingsPage',
      auth: true,
      authPage: 'SigninPage',
      component: NotificationSettingsPage,
      loadDuck: pageDataLoadingAPI.NotificationSettingsPage.loadDuck,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'SigninPage',
      component: StripePayoutPage,
      loadDuck: pageDataLoadingAPI.StripePayoutPage.loadDuck,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'SigninPage',
      component: PaymentMethodsPage,
      loadDuck: pageDataLoadingAPI.PaymentMethodsPage.loadDuck,
    },
    {
      path: '/account/pass',
      name: 'PassSettingsPage',
      auth: true,
      authPage: 'SigninPage',
      component: PassSettingsPage,
      loadDuck: pageDataLoadingAPI.PassSettingsPage.loadDuck,
    },
    {
      path: '/account/delete-user',
      name: 'DeleteUserPage',
      auth: true,
      authPage: 'SigninPage',
      component: DeleteUserPage,
      loadDuck: pageDataLoadingAPI.DeleteUserPage.loadDuck,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: (props) => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'SigninPage',
      component: EmailVerificationPage,
      loadDuck: pageDataLoadingAPI.EmailVerificationPage.loadDuck,
    },
    {
      path: '/currentLocation',
      name: 'CurrentPosition',
      component: () => (
        <NamedRedirect
          name="SearchPage"
          params={`?${new URLSearchParams(searchParams).toString()}`}
          state={{ currentLocation: true }}
        />
      ),
    },
    {
      path: '/entsorgungsstation',
      name: 'DumpStationSearchPage',
      component: DumpStationSearchPage,
      loadDuck: pageDataLoadingAPI.DumpStationSearchPage.loadDuck,
    },
    {
      path: '/entsorgungsstation/:id',
      name: 'DumpStationPage',
      component: DumpStationPage,
      loadDuck: pageDataLoadingAPI.DumpStationPage.loadDuck,
    },
    {
      path: '/:slug/*',
      name: 'StoryblokPage',
      component: StoryblokPage,
      loadDuck: pageDataLoadingAPI.StoryblokPage.loadDuck,
    },
  ];
};

export default routeConfiguration;
